import type { Address } from "@redotech/account-common/address";
import type { Portal } from "@redotech/account-common/portal";
import type { BigcommerceStorefrontSettings } from "@redotech/bigcommerce-storefront-catalog/settings";
import { ThirdPartyRefundProvider } from "@redotech/congo-enums/third-party-refund-provider";
import type { FeatureFlagKey } from "@redotech/feature-flags-model/keys";
import { Jsonified } from "@redotech/json/json";
import { ShopperFacingBillingStatus } from "@redotech/redo-billing-common/shopper-facing-billing-status";
import { ConciergeProductFormSettings } from "@redotech/redo-concierge-settings-common/concierge-product-form";
import { BrandKit } from "@redotech/redo-content-builder-common/brand-kit";
import { ConversionAutocheck } from "@redotech/redo-conversion-core-common/conversion/conversion-autocheck";
import { HsaAutocheck } from "@redotech/redo-conversion-core-common/conversion/hsa-autocheck";
import { CartOptimizationSettings } from "@redotech/redo-conversion-settings-common/cart-optimization-settings";
import type { CheckoutButtonsFeeProductBehavior } from "@redotech/redo-conversion-settings-common/checkout-buttons-fee-product-behavior";
import type {
  CoverageRescueDiscount,
  RescueModalUi,
} from "@redotech/redo-conversion-settings-common/predictive-conversion-settings";
import { SaveForLaterSlideInPanelAnchorLocation } from "@redotech/redo-customer-accounts-common/save-for-later-slide-in-panel-anchor-location";
import { LoyaltyAutocheck } from "@redotech/redo-loyalty-common/loyalty-autocheck";
import type { BackInStockInsertionMode } from "@redotech/redo-marketing-settings-common/back-in-stock-insertion-mode";
import type { OrderLookupSettings } from "@redotech/redo-order-tracking-lookup-common/order-lookup-defaults";
import { ExchangeMetafieldDisplayConfig } from "@redotech/redo-return-core-common/exchange-metafield-display";
import { RefundIntegrationSummary } from "@redotech/redo-return-core-common/refund-destination/eligibility";
import {
  Coverage,
  PaymentModel,
} from "@redotech/redo-return-coverage-common/coverage";
import type { CoverageMlModelDb } from "@redotech/redo-return-coverage-common/coverage-calculator/coverage-ml/registry-schema";
import type {
  CoverageCreditIncentive,
  DynamicReturnPricing,
  InternationalReturnPricing,
  PackageProtectionPricePerItemPricing,
  PackageProtectionPricingStrategy,
  PricingRuleSet,
} from "@redotech/redo-return-coverage-common/coverage-types";
import type { Automation } from "@redotech/redo-return-settings-common/automation";
import type { ContextualAutocheck } from "@redotech/redo-return-settings-common/contextual-settings";
import type {
  ExtendedWarrantyCartToggleExperience,
  ExtendedWarrantyPdpRenderMethod,
  ExtendedWarrantyPopupExperience,
} from "@redotech/redo-return-warranties-common/extended-warranty-enable-criteria";
import type { StorefrontAutocheckTreatment } from "@redotech/redo-storefront-ab-test-common/storefront-ab-test";
import type { AttachmentStrategy } from "@redotech/redo-storefront-common/attachment-strategy";
import type { CartRefreshAction } from "@redotech/redo-storefront-common/cart-refresh-action";
import type { CheckboxIconPosition } from "@redotech/redo-storefront-common/checkbox-icon-position";
import type { CheckoutMethod } from "@redotech/redo-storefront-common/checkout-method";
import {
  CheckoutUICustomizationSettings,
  Icons,
} from "@redotech/redo-storefront-common/checkout-ui-customization";
import type { CoveragePlacementMetrics } from "@redotech/redo-storefront-common/coverage-placement-metrics";
import type { FraudZipCoverageExclusionStrategy } from "@redotech/redo-storefront-common/fraud-zip-coverage-exclusion-strategy";
import type { HideRedoConfig } from "@redotech/redo-storefront-common/hide-redo-config";
import { InfoModal } from "@redotech/redo-storefront-common/info-modal";
import { InsertionPosition } from "@redotech/redo-storefront-common/insertion-position";
import type { StorefrontConfig } from "@redotech/redo-storefront-common/storefront-config";
import type { ToggleFields } from "@redotech/redo-storefront-common/toggle-fields";
import {
  CartValueCalculationMethod,
  ClickStandardCheckoutButton,
  ImageElementType,
  PlacementType,
  PriceLocation,
  TermsAndConditions,
  WidgetElementType,
} from "@redotech/redo-storefront-common/widget-config";
import type { WidgetPlacementsInfo } from "@redotech/redo-storefront-common/widget-schema";
import type { SellingPlanGroup } from "@redotech/redo-storefront-common/widget-selling-plan";
import type { StorefrontPublishStatusMap } from "@redotech/redo-team-common/storefront-publish";
import type {
  Settings,
  ShopperFacingBillingInfo,
} from "@redotech/redo-team-common/team";
import { TnplContractTemplateInAutoCheck } from "@redotech/redo-tnpl-common/tnpl-contract-template";
import type { TnplWidgetSettings } from "@redotech/redo-tnpl-settings-common/tnpl-settings";
import { CartProductType } from "./cart-product-types";
import type { PackageProtectionInternationalInsurance } from "./package-protection-insurance";
import { TreatmentDoc } from "./split";

export const CURRENT_WIDGET_PLACEMENTS_INFO_VERSION = 1;

export enum ButtonSettingPerProduct {
  BOTH = "both",
  RETURN = "return",
  PACKAGE_PROTECTION = "packageProtection",
}

export const buttonSettingPerProductToDisplayText: Record<
  ButtonSettingPerProduct,
  string
> = {
  [ButtonSettingPerProduct.BOTH]: "Returns & package protection",
  [ButtonSettingPerProduct.RETURN]: "Returns only",
  [ButtonSettingPerProduct.PACKAGE_PROTECTION]: "Package protection only",
};

export interface TitleSubtitle {
  titleText?: string;
  subtitleText?: string;
}
export interface CoverageText {
  returns?: TitleSubtitle;
  packageProtection?: TitleSubtitle;
  both?: TitleSubtitle;
}

export interface NonCoverageButtonText {
  returns?: string;
  packageProtection?: string;
  both?: string;
}

export interface CoverageButtonStyles {
  color?: string;
  fontColor?: string;
  subtitleFontColor?: string;
  fontSize?: number;
  subtitleFontSize?: number;
  nonCoverageFontColor?: string;
  nonCoverageFontSize?: number;
  includeNonCoverageUnderline?: boolean;
}

export interface InfoCardStyles {
  color?: string;
  titleFontSize?: number;
  subtitleFontSize?: number;
  titleFontColor?: string;
  subtitleFontColor?: string;
}

interface BaseWidgetConfig {
  _id?: string;
  elementType: WidgetElementType;
  name?: string;
  /**
   * @deprecated Prefer `placement.list[].cssSelector`. When `placement` is
   * defined the list of placements drives DOM targeting and this value is
   * ignored.
   */
  placementSelector: string;
  placementType?: PlacementType;
  /**
   * @deprecated Prefer `placement.list[].insertionPosition`. When `placement`
   * is defined the list of placements drives insertion position and this value
   * is ignored.
   */
  insertionPosition?: InsertionPosition;
  shadowRootSelector?: string;
  placeInsideAndOutsideShadowRoot?: boolean;
  dataTarget?: string;
  localizationId?: string;
  /**
   * Multi-placement configuration. When set (and non-empty) it supersedes the
   * legacy `placementSelector` / `insertionPosition` fields and allows a single
   * widget config to be rendered into multiple DOM locations, each with its
   * own selector and insertion position.
   *
   * If unset, the widget continues to use the legacy single-placement fields.
   */
  placement?: WidgetPlacementsInfo;
}

export interface IndividualCheckoutButtons extends BaseWidgetConfig {
  elementType:
    | WidgetElementType.COVERAGE_CHECKOUT_BUTTON
    | WidgetElementType.NON_COVERAGE_CHECKOUT_BUTTON;
  content: string; // HTML, with template variables
  clickStandardCheckoutButton?: ClickStandardCheckoutButton;
}

export interface BothCheckoutButtons extends BaseWidgetConfig {
  elementType: WidgetElementType.BOTH_CHECKOUT_BUTTONS;
  coverageButtonText?: CoverageText;
  nonCoverageButtonText?: NonCoverageButtonText;
  buttonSettingPerProduct?: ButtonSettingPerProduct;
  coverageButtonStyles?: CoverageButtonStyles;
  includeNonCoverageUnderline?: boolean;
  optOutButtonFirst?: boolean;
  clickStandardCheckoutButton?: ClickStandardCheckoutButton;
}

export const priceLocationToDisplayText = {
  [PriceLocation.NONE]: "None",
  [PriceLocation.TITLE]: "Title",
  [PriceLocation.RIGHT]: "Right",
};

export interface InfoCard extends BaseWidgetConfig {
  elementType: WidgetElementType.INFO_CARD;
  cardText?: CoverageText;
  showInfoIcon: boolean;
  badgeWord?: string;
  infoCardStyles?: InfoCardStyles;
  imageElement?: ImageElementType;
  priceLocation?: PriceLocation;
  showBackground?: boolean;
  infoButtonCustomAttributes?: Record<string, string>;
}

export function getCardStyleLabel(showBackground: boolean) {
  return showBackground ? "Card with background" : "Inline card";
}

export interface SingleToggleWidgetConfig extends BaseWidgetConfig {
  elementType: WidgetElementType.SINGLE_TOGGLE;
}

export interface CartCheckboxWidgetConfig extends BaseWidgetConfig {
  elementType: WidgetElementType.CART_CHECKBOX;
}

export interface DoubleToggleWidgetConfig extends BaseWidgetConfig {
  elementType: WidgetElementType.DOUBLE_TOGGLE;
}

export interface CartCardWidgetConfig extends BaseWidgetConfig {
  elementType: WidgetElementType.CART_CARD;
}

export interface CheckoutButtonWidgetConfig extends BaseWidgetConfig {
  elementType: WidgetElementType.CHECKOUT_BUTTON;
}

export type WidgetConfig =
  | InfoCard
  | BothCheckoutButtons
  | IndividualCheckoutButtons
  | SingleToggleWidgetConfig
  | CartCheckboxWidgetConfig
  | DoubleToggleWidgetConfig
  | CartCardWidgetConfig
  | CheckoutButtonWidgetConfig;

export interface LocalizationGroup {
  _id: string;
  name: string;
  countries: string[];
  languages: string[];
}

export interface LocalizationTextOverride {
  localizationGroupId: string;
  localizationObjectId: string;
  path: string;
  text: string;
}

/**
 * SFCC storefront client config passed through autocheck. A static backup —
 * prefer a cartridge-injected runtime locale for dynamic multi-locale sites.
 */
export interface SfccStorefrontClientConfig {
  /** SFCC site id, used to build the `Sites-<siteId>-Site` cart URL. */
  siteId: string;
  /** Backup SFCC locale segment (e.g. `en_AU`). */
  locale: string;
}

export interface WidgetAutocheck {
  teamId?: string;
  widgetSlug?: string;
  noDebounceButtonRendering?: boolean;
  exchangeBannerPositionBottom?: boolean;
  exchangeBannerInsertBeforeBody?: boolean;
  icon?: Icons;
  iconColor?: string;
  iconBackgroundColor?: string;
  displayIcon: boolean;
  checkboxIconPosition?: CheckboxIconPosition;
  storefrontAccessToken: string;
  multistorefront?: StorefrontConfig[];
  storefrontClient?: { sfcc?: SfccStorefrontClientConfig };
  html?: string | null;
  checkboxText?: string | null;
  logo?: string | null;
  enableTopLayerModalApi?: boolean;
  toggleTextOptions?: {
    returnToggle?: ToggleFields;
    packageProtectionToggle?: ToggleFields;
    bothProductToggle?: ToggleFields;
  };
  groupNearbyToggleElementsTogether?: boolean;
  checkboxTextAfterRedo?: string | null;
  checkoutSelector?: string;
  exchangeBannerColor?: string;
  exchangeBannerText?: string;
  exchangeBannerFont?: string;
  exchangeBannerFontColor?: string;
  exchangeBannerShowDoneButton: boolean;
  exchangeBannerCustomCss?: string;
  disableRedoForCustomer: boolean;
  /** Kill switch: skip re-adding coverage if the customer removed it mid-ensure-cycle. Default ON when absent. */
  respectForcedRemovalDuringEnsure?: boolean;
  cartResponseCaptureEnabled?: boolean;
  coverageProductIds?: string[];
  mysteryGiftVariantId?: string;
  coverageExcludeCollections: string[];
  coverageExcludeDiscountCodes: string[];
  coverageExcludeProperties: string[];
  coverageExcludedVariantMetafields?: string[];
  returnsCoverageExcludedSkus?: string[];
  excludeCoverageIfAnyInvalidProduct?: boolean;
  locationBasedAutocheckRemoval?: { enabled: boolean };
  redoAutoCheck: boolean;
  selector: string;
  paidModel: PaymentModel;
  textColor: string;
  coverage: Coverage;
  coveragePlacementMetrics?: CoveragePlacementMetrics;
  /**
   * This boolean represents whether we should sell returns coverage to items that are eligible for returns.
   */
  coverageEnabled: boolean;
  /**
   * Windows in which Redo staff have paused this merchant's coverage. Passed
   * to the coverage calculator, which reports coverage ineligible while a
   * window is running; the merchant's own settings above are unchanged.
   */
  coveragePausedDates?: { start: string; end: string }[];
  enabled: boolean;
  /**
   * Per-product storefront publish status, copied verbatim from
   * `team.settings.storefrontPublishStatus`. Absent keys default to LIVE_SITE.
   * Consumed by `passesStorefrontPublishGate` to gate rendering by theme.
   */
  storefrontPublishStatus?: StorefrontPublishStatusMap;
  finalSaleTags: string[];
  hideCheckboxBranding: boolean;
  hideCheckbox: boolean;
  hideCheckboxPrice: boolean;
  countries: string[];
  countriesPackageProtection: string[];
  countriesFinalSaleReturns: string[];
  submitButtonSelector?: string;
  modalTextAdjustments: {
    text1: string;
    title1: string;
    text2: string;
    title2: string;
    text3: string;
    title3: string;
  };
  infoModal?: InfoModal;
  modalLogo?: string;
  /**
   * Merchant's return policy URL. Resolved from portal customization, falling
   * back to the misnamed legacy `team.portal.privacy_link` field for
   * un-migrated merchants. Used to render "Merchant's Return Policy" as a
   * link in the info modal footer when present.
   */
  returnPolicyUrl?: string;
  shopSiteUseCartDiscounts?: boolean;
  excludeProductTags?: string[];
  excludeProductSkus?: string[];
  cartValueCalculationMethod?: CartValueCalculationMethod;
  excludedItemProperties?: { name: string; value: string }[];
  cartToggle: {
    enabled: boolean;
    useShadowRootInCart?: boolean;
    cartShadowRootSelector?: string;
    cartToggleSelector?: string;
    cartToggleShadowRootSelector?: string;
    cartClickListenerSelector?: string;
    toggleOnColor: string;
    toggleOffColor: string;
    toggleCircleColor: string;
    cartTogglePlacement?: InsertionPosition;
    cartReplaceItems?: boolean;
  };
  widgetConfigs?: WidgetConfig[];
  hideRedoProduct?: HideRedoConfig;
  checkoutExtensionEnabled: boolean;
  checkoutAnalyticsInCheckoutToggleWidgetEnabled?: boolean;
  fraudZipCoverageExclusionStrategy?: FraudZipCoverageExclusionStrategy;
  checkboxFontSize?: number;
  toggleFontSize?: number;
  infoIconLink?: string;
  attachmentStrategy: AttachmentStrategy;
  toggleSubtextEnabled: boolean;
  packageProtectionAutoCheck: boolean;
  protectionExcludeTags?: string[];
  usingCartAndCheckoutToggle?: boolean;
  forceDoubleCheckoutToggle?: boolean;
  setCheckoutToggleBasedOnOptInCartAttribute?: boolean;
  hideCheckoutUiWhenOptedIn?: boolean;
  openShadowRoot?: boolean;
  packageProtection: {
    /**
     * This boolean represents whether we should sell package protection to items that are eligible for package protection.
     */
    coverageEnabled: boolean;
    packageProtectionPlusEnabled: boolean;
    percentage: number;
    splitProducts: boolean;
    pricingRuleSets?: PricingRuleSet[];
    // Optional at runtime: cached configs from older servers omit these, so
    // readers must default to cart-value pricing.
    pricingStrategy?: PackageProtectionPricingStrategy;
    pricePerItem?: PackageProtectionPricePerItemPricing;
    minPrice: number;
    maxPrice: number;
    minCoverageEligibleCartTotal?: number;
    maxCoverageEligibleCartTotal?: number;
    packageProtectionPlusMerchantPaidPerOrderEnabled?: boolean;
    packageProtectionPlusMerchantPaidEnabledDates?:
      | { start: string | null; end?: string | null }[]
      | null;
    packageProtectionMerchantPaidCartThreshold?: number;
    fixedSubscriptionPrice?: number;
    coverageMlModel?: CoverageMlModelDb;
    shouldRecordPackageProtectionExposure?: boolean;
    // Optional at runtime: cached widget configs and payloads served by an
    // older server version may omit this, so all readers must default it.
    coverageExcludedCollections?: string[];
    coverageExcludedVariantMetafields?: string[];
    // Optional at runtime: set only when the team's flag routes shipments
    // that are not US-to-US to UPS Capital; cached payloads may omit it.
    internationalInsurance?: PackageProtectionInternationalInsurance;
  };
  predictedCountry?: string;
  customJs?: { enabled: boolean; script: string };
  customPdpCss?: string;
  customToggleCss?: string;
  treatments?: Jsonified<TreatmentDoc>[] | undefined;
  /**
   * Arms of the account's running storefront A/B test. When present they take
   * priority over the legacy `treatments`.
   */
  storefrontTreatments?: StorefrontAutocheckTreatment[] | undefined;
  forceIPAddressLocation?: boolean;
  discountCodePartialMatch?: boolean;
  excludePickup: boolean;
  dynamicReturnsPricing?: DynamicReturnPricing | null;
  preProtectedOrdersEnabled?: boolean;
  checkoutUICustomizations?: CheckoutUICustomizationSettings;
  internationalReturnsPricing?: InternationalReturnPricing | null;
  automation?: Automation;
  currencyExchangeRate?: number;
  finalSaleReturns: {
    /**
     * This boolean represents whether we should sell final sale return item coverage
     * on items that are eligible for final sale return and also sell label coverage
     * to carts with final sale return items.
     */
    coverageEnabled: boolean;
    coverageValidTags: string[];
    coverageValidCollections: string[];
    coverageValidCollectionTitles?: string[];
    percentage: number;
    pricingRuleSets?: PricingRuleSet[];
    minPrice?: number;
    maxPrice: number;
    shippingLabelCoveragePrice?: number;
    allCovered: boolean;
    allEligible: boolean;
    eligibleDiscountThreshold?: number | null | undefined;
    minCoverageEligibleCartTotal?: number;
    maxCoverageEligibleCartTotal?: number;
  };
  customerAccounts: {
    enabled: boolean;
    saveForLaterPanel: {
      enabled: boolean;
      numViewedProductsUntilPanelOpen: number | undefined;
      title: string | undefined;
      message: string | undefined;
      anchorLocation: SaveForLaterSlideInPanelAnchorLocation | undefined;
    };
  };
  showIcons?: boolean;
  customerWidgetEnabled: boolean;
  chatWidgetScriptTagInstalled: boolean;
  /** Absent on payloads from before the embed took over loading Contextual. */
  contextual?: ContextualAutocheck;
  reviews: { enabled: boolean };
  orderTrackingLogin?: {
    enabled: boolean;
    settings?: OrderLookupSettings | null;
    loadingBackgroundColor?: string | null;
  };
  pricePerOrder?: string;
  brandKit: BrandKit;
  extendedWarranties?: {
    enabled: boolean;
    cartToggleExperience: ExtendedWarrantyCartToggleExperience;
    pdpRenderMethod: ExtendedWarrantyPdpRenderMethod;
    popupExperience: ExtendedWarrantyPopupExperience;
  };
  concierge: {
    enabled: boolean;
    placeConciergeProductForm: boolean;
    conciergeProductFormSettings?: ConciergeProductFormSettings | undefined;
  };
  marketing: {
    enabled: boolean;
    subscribeFromSaveForLater: boolean;
    numEmbeddedFormsConfigured?: number;
    hasPublishedSignupForms?: boolean;
    hasEnabledNonEmbeddedSpotlights?: boolean;
    backInStockButtonSelector?: string;
    backInStockInsertionMode?: BackInStockInsertionMode;
  };
  recover: { enabled: boolean };
  /**
   * Checkout delivery-estimates (EDD) renderer gate; authoritative gating
   * lives in the commerce-cloud getDeliveryEstimates RPC.
   */
  checkoutDeliveryEstimates: {
    enabled: boolean;
    suppressCartItemFields?: string[];
    suppressConfirmationSelectors?: string[];
  };
  conversion: ConversionAutocheck;
  hsa: HsaAutocheck;
  loyalty: LoyaltyAutocheck;
  termsAndConditions?: TermsAndConditions;
  clickStandardCheckoutButton?: ClickStandardCheckoutButton;
  addProductOnOptInClick?: boolean;
  checkoutButtonsFeeProductBehavior?: CheckoutButtonsFeeProductBehavior;
  useBuyNowCoverage?: boolean;
  buyNow?: { customCss?: string };
  tryFirst?: {
    sellingPlanId?: string;
    sellingPlanGroupId?: string;
    widget?: TnplWidgetSettings;
    template?: TnplContractTemplateInAutoCheck;
    useMinutesForTrialPeriod?: boolean;
  };
  cartRewards?: {
    threshold: number;
    type: string;
    approachingThresholdMessage?: string;
    pastThresholdMessage?: string;
    placementSelector?: string;
    enabled?: boolean;
  };
  cartTotals?: {
    placementSelector?: string;
    enabled?: boolean;
    customCss?: string;
  };
  cartOptimization?: CartOptimizationSettings;
  autoNavigateToCheckoutAfterTimeout?: boolean;
  checkoutMethod?: CheckoutMethod;
  rescueCheckoutButtonsEnabled?: boolean;
  rescuePanelUi?: RescueModalUi;
  rescuePanelCustomCss?: string;
  rescuePanelDiscount?: CoverageRescueDiscount;
  shouldNotUseStorefrontApi?: boolean;
  disableBusinessAccountInStorefront?: boolean;
  preventPageRefreshAfterCartAdd?: boolean;
  dispatchResizeAfterItemsPlaced?: boolean;
  /** When any actions are set, they replace the built-in theme refresh hooks. */
  cartRefreshActions?: CartRefreshAction[];
  coverageCreditIncentive?: CoverageCreditIncentive;
  discountsAreFinalSales?: boolean;
  couponedItemsDiscountThreshold?: number;
  purpleDotEnabled?: boolean;
  dynamicCoverageTransformFunctionEnabled?: boolean;
  billing: ShopperFacingBillingInfo;
  localizationGroups?: LocalizationGroup[];
  localizationTextOverrides?: LocalizationTextOverride[];
  storefrontLoggingAllowedPercentage?: number;
  visitorObservationEnabled?: boolean;
  /** The team's setting; the page's copy comes from the env snippet. */
  storefrontConsentEnabled?: boolean;
  localizationObjectId: string;
  // TODO: migrate customer tag based text overrides to Coverage Policies once built. See redo/merchant/app/src/setting/coverage/coverage-builder/left-panel/widget-pages/advanced-tab.tsx. $300k deal I'm sorry we needed this built super fast I tried to make it easily migrateable -Josh
  customerTagTextOverride?: {
    enabled: boolean;
    customerTags: string[];
    coverageTitle?: string;
    coverageSubtext?: string;
  };
}

export function getAutocheckLocalizationId(teamId: string) {
  // WARNING: if you edit this function you must perform a database migration to update the localizationId field for all autochecks.
  return `autocheck-${teamId}`;
}

export const showCoverageBasedOnBillingStatus: Record<
  ShopperFacingBillingStatus,
  boolean
> = {
  [ShopperFacingBillingStatus.UP_TO_DATE]: true,
  [ShopperFacingBillingStatus.MISSING_BILLING_INFO]: false,
  [ShopperFacingBillingStatus.BILLING_FAILURE]: false,
};

export const defaultTermsAndConditionsBlockedMessage =
  "Please agree to the terms and conditions before checking out";

export interface feeProduct {
  productId: number;
  variants: { variant_id: string; price: number }[];
  handle: string;
  type: Exclude<CartProductType, CartProductType.ExtendedWarranty>;
  sellingPlanGroups: SellingPlanGroup[];
}

export interface WidgetFees {
  price: string;
  handle: string;
  enabled: boolean;
  productId: number;
  variantId: number;
  mainType: Exclude<CartProductType, CartProductType.ExtendedWarranty>;
  splitProducts: boolean;
  returnProduct?: feeProduct;
  packageProduct?: feeProduct;
  bothProduct?: feeProduct;
}

export interface GetWidgetResponse {
  widget_slug: string;
  name: string;
  storeUrl: string;
  theme: Portal;
  settings: Settings;
  service_active: boolean;
  service_disabled_reason: string | null;
  credit_value: number | null;
  storefrontAccessToken: string | null;
  headlessAccessToken: string | undefined;
  hasStorefrontAccess: boolean;
  customerEmail: string;
  automation: Automation;
  address?: Address;
  customInStoreAddresses?: Address[];
  /**
   * Loading background color for the embedded tracking iframe. Sourced from
   * the per-team TrackingPageGlobalSettings collection. Shared across all
   * tracking pages for the team.
   */
  loadingBackgroundColor?: string;
  /** When true, hides breadcrumb nav and account dropdown on all tracking pages. */
  hideTrackingPageNavigation?: boolean;
  /** When true, hides the entire portal navbar on all tracking pages. */
  hideTrackingPageNavbar?: boolean;
  featureFlags?: Partial<Record<FeatureFlagKey, boolean>>;
  /** Redo-controlled entitlement: wallet rails a refund may be sent to. */
  redoRefundProviders?: ThirdPartyRefundProvider[];
  /**
   * The team's refund-capable integrations. Only meaningful for custom
   * integrations, where whether a refund can reach the original payment method
   * depends on the team having a provider that can settle the order's gateway.
   */
  refundIntegrations?: RefundIntegrationSummary[];
  /** Numeric Shopify product ids of the team's Redo coverage products. */
  redoCoverageProductIds?: string[];
  /** Present for teams with an enabled BigCommerce integration. */
  bigcommerceStorefront?: BigcommerceStorefrontSettings;
  shopifyMetafieldsToDisplayOnExchangeItems?: ExchangeMetafieldDisplayConfig[];
  isDemoStore?: boolean;
}

export const defaultInfoCardWidget: InfoCard = {
  elementType: WidgetElementType.INFO_CARD,
  placementType: PlacementType.CART,
  cardText: undefined,
  showInfoIcon: true,
  placementSelector: "",
  showBackground: false,
};

export const defaultBothCheckoutButtonsWidget: BothCheckoutButtons = {
  elementType: WidgetElementType.BOTH_CHECKOUT_BUTTONS,
  placementType: PlacementType.CART,
  placementSelector: "",
  coverageButtonText: undefined,
  nonCoverageButtonText: undefined,
  coverageButtonStyles: undefined,
};

export const defaultWidgets: WidgetConfig[] = [
  defaultInfoCardWidget,
  defaultBothCheckoutButtonsWidget,
];

/** Checkout button text that stores run by Shopify employees start with. */
export const SHOPIFY_STORE_DEFAULT_CHECKOUT_BUTTON_TEXT =
  "Checkout+ | Adds %price%";

const shopifyStoreDefaultCheckoutButtonText: TitleSubtitle = {
  titleText: SHOPIFY_STORE_DEFAULT_CHECKOUT_BUTTON_TEXT,
  subtitleText: "",
};

export const shopifyStoreDefaultBothCheckoutButtonsWidget: BothCheckoutButtons =
  {
    ...defaultBothCheckoutButtonsWidget,
    coverageButtonText: {
      returns: shopifyStoreDefaultCheckoutButtonText,
      packageProtection: shopifyStoreDefaultCheckoutButtonText,
      both: shopifyStoreDefaultCheckoutButtonText,
    },
  };

const BIGCOMMERCE_CHECKOUT_BUTTONS_SELECTOR =
  ".cart-actions, .previewCartCheckout, .previewCartAction";

const BIGCOMMERCE_TOGGLE_SELECTOR =
  ".optimizedCheckout-orderSummary-cartSection:nth-of-type(3)";

export const bigcommerceDefaultInfoCardWidget: InfoCard = {
  elementType: WidgetElementType.INFO_CARD,
  placementType: PlacementType.CART,
  insertionPosition: InsertionPosition.PREPEND,
  placementSelector: BIGCOMMERCE_CHECKOUT_BUTTONS_SELECTOR,
  cardText: undefined,
  showInfoIcon: true,
};

export const bigcommerceDefaultBothCheckoutButtonsWidget: BothCheckoutButtons =
  {
    elementType: WidgetElementType.BOTH_CHECKOUT_BUTTONS,
    placementType: PlacementType.CART,
    insertionPosition: InsertionPosition.PREPEND,
    placementSelector: BIGCOMMERCE_CHECKOUT_BUTTONS_SELECTOR,
    coverageButtonText: undefined,
    nonCoverageButtonText: undefined,
    coverageButtonStyles: undefined,
  };

export const bigcommerceDefaultToggleWidget: SingleToggleWidgetConfig = {
  elementType: WidgetElementType.SINGLE_TOGGLE,
  placementType: PlacementType.CART,
  insertionPosition: InsertionPosition.APPEND,
  placementSelector: BIGCOMMERCE_TOGGLE_SELECTOR,
};

export const bigcommerceDefaultWidgets: WidgetConfig[] = [
  bigcommerceDefaultInfoCardWidget,
  bigcommerceDefaultBothCheckoutButtonsWidget,
  bigcommerceDefaultToggleWidget,
];

export const isCheckoutButtonsOrInfoCards = (widgetConfig?: WidgetConfig) => {
  return (
    widgetConfig &&
    [
      WidgetElementType.COVERAGE_CHECKOUT_BUTTON,
      WidgetElementType.NON_COVERAGE_CHECKOUT_BUTTON,
      WidgetElementType.BOTH_CHECKOUT_BUTTONS,
      WidgetElementType.INFO_CARD,
    ].includes(widgetConfig.elementType)
  );
};

/**
 * A fully-resolved DOM placement for a single widget. Both fields are
 * required: callers expect to read `cssSelector` and `insertionPosition`
 * directly without further fallback chains. Resolution (collapsing the new
 * `placement` field, the legacy `placementSelector` / `insertionPosition`
 * fields, autocheck-level defaults, and constant defaults into a single
 * concrete value) happens in the extension layer that has access to those
 * defaults — see `resolveWidgetPlacements` in
 * `redo/shopify/extension/src/utils/element-placement.tsx`.
 */
export interface ResolvedWidgetPlacement {
  cssSelector: string;
  insertionPosition: InsertionPosition;
  shadowRootSelector?: string;
}

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export {
  type PlacementDescriptor,
  type WidgetPlacementsInfo,
} from "@redotech/redo-storefront-common/widget-schema";

export const WIDGET_TEMPLATE_VARIABLES_TOOLTIP_TEXT = `available Variables: \n
%cartTotal%,
%cartTotalPreDiscount%,
%combinedPrice%,
%combinedPricePreDiscount%,
%price%,
%coverageCreditAmount%,
%compareAtPrice%,
%compareAtPricePlusRedo%,
--------------------------------------------------
if statements:
%if coverageCredit%,
%if var1 < var2%,
%else%,
%endif%,
`;
